/**
 * Created by @author @ddennis - ddennis.dk aka fantastisk.dk/works aka meresukker.dk on 08/05/2023.
 */
import { useParams } from 'react-router-dom'
import { AuditFactory } from '../../util/auditFactory'
import { Button, Pagination, Steps } from 'antd'
import { useSubjectAudit } from '../../hooks/useSubjectAudit'
import React, { Suspense, useLayoutEffect, useRef } from 'react'
import { useSubject } from '../../hooks/useSubject'
import { useProtocol } from '../../hooks/useProtocol'
import { SUBJECT_STATUS_ENUM } from '../../Types'
import { buildSteps, getCurrentStep } from '../../util/subject-journey'
import { useEligibilityActions } from '../../hooks/useEligibilityActions'
import { ENDPOINTS } from '../../service/ENDPOINTS'
import { Auth } from 'aws-amplify'
import { SpinnerFullHeight } from '../../components/spinner/SpinnerFullHeight'
import dayjs from 'dayjs'
import { mutate as mutateGlobal } from 'swr/_internal'
import { PAGINATION_LIMIT } from '../../constants'

type Props = {
  onLoaded?: (value) => void
}
export const SubjectAuditTrial = ({ onLoaded }: Props) => {
  const { projectId, subjectId } = useParams() as { projectId: string; subjectId: string }

  const { data: protocolData } = useProtocol(projectId)
  const { data: subjectData } = useSubject(projectId, subjectId)
  const { data, setPageNumber, pageNumber, limit, isValidating } = useSubjectAudit(projectId, subjectId)
  const { auditEvents, totalCount } = data

  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (data && !isValidating && onLoaded) {
      const k = ref.current ? ref.current.getBoundingClientRect().height : 0

      onLoaded(k)
    }
  }, [data, isValidating, onLoaded])

  const onChangePage = (pageNumber: number) => {
    setPageNumber(pageNumber)
  }

  const currentStep = getCurrentStep(protocolData.phases, subjectData)
  const items = buildSteps(protocolData.phases, subjectData)

  const subjectStatus = subjectData.status === SUBJECT_STATUS_ENUM.TERMINATED ? 'error' : 'process'

  return (
    <div className="container-fluid" ref={ref}>
      <div className="row border border-light bg-white  rounded-2 mb-3" style={{}}>
        <div className="col-12 py-4 px-4">
          <Steps
            orientation="horizontal"
            status={subjectStatus}
            current={subjectData.status === SUBJECT_STATUS_ENUM.COMPLETED ? currentStep + 1 : currentStep}
            items={items}
          />
        </div>
        <div className="col-12 m-0 p-0">
          <Suspense fallback={<SpinnerFullHeight fullHeight={true}></SpinnerFullHeight>}>
            <EligibilityCalculations projectId={projectId} subjectId={subjectId} />
          </Suspense>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-3 d-flex justify-content-center align-items-center">
          {/*totalCount is undefined until data has been loaded in initial render*/}
          <div className={totalCount ? 'visible' : 'invisible'}>
            <Pagination
              key={pageNumber}
              defaultCurrent={pageNumber}
              defaultPageSize={limit}
              showSizeChanger={false}
              total={totalCount}
              onChange={onChangePage}
            />
          </div>
        </div>
      </div>
      <AuditFactory data={auditEvents}></AuditFactory>
      <div className="row" style={{}}>
        <div className="col-12 d-flex justify-content-center ">
          <Pagination
            key={pageNumber}
            defaultCurrent={pageNumber}
            defaultPageSize={limit}
            showSizeChanger={false}
            total={totalCount}
            onChange={onChangePage}
          />
        </div>
      </div>
    </div>
  )
}

const EligibilityCalculations = ({ projectId, subjectId }: { projectId: string; subjectId: string }) => {
  const { data: eligibilityActions } = useEligibilityActions(projectId, subjectId)

  const downloadEligibilityPdf = async (actionId: string) => {
    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken()
      const p = ENDPOINTS.SUBJECTS.downloadEligibilityPdf(projectId, subjectId, actionId)

      // Send an authenticated GET request to the file download endpoint.
      const response = await fetch(p, {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })

      if (!response.ok) {
        const errorBody = await response.json()
        throw new Error(errorBody.message)
      }

      // Read the Content-Disposition header, that contain the original filename
      const contentDisposition = response.headers.get('Content-Disposition')

      // Extract the filename from the Content-Disposition header.
      const fileName = contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ?? `eligibility-download-${subjectId}.pdf`

      // Convert the response body into a Blob.
      // A Blob represents the downloaded binary file data in the browser.
      const blob = await response.blob()

      // Create a temporary object URL that points to the downloaded Blob.
      // This URL can be used as the href of a link element.
      const url = URL.createObjectURL(blob)

      // Create a link that will be used to trigger the download.
      const link = document.createElement('a')
      link.href = url
      link.rel = 'noopener noreferrer'

      // Set the browser download filename.
      // And add the link to the DOM so it can be clicked programmatically.
      link.download = fileName
      document.body.appendChild(link)

      // Start the browser download.
      link.click()

      // Remove link element from the DOM
      link.remove()

      // Release the temporary object URL to avoid leaking memory.
      URL.revokeObjectURL(url)

      // Update audit trail list
      mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
    } catch (error: any) {
      alert(`Download failed: ${error.message}`)
    }
  }

  if (!eligibilityActions?.length) return null

  return (
    <div className="row g-0">
      <div className="col-5 border-top d-flex py-2 align-items-center justify-content-between">
        <p className="p-small ps-4 w-50">Report Type</p>
        <p className="p-small w-50 opacity-75">Generated On</p>
      </div>
      <div className="col-7 border-top d-flex py-2 align-items-center justify-content-between">
        <p className="p-small opacity-75">Generated By</p>
      </div>

      {eligibilityActions.map((action: any) => {
        const time = `${dayjs.utc(action.createdAt).format('DD/MM/YYYY HH:mm')} UTC`

        return (
          <div key={action._id.toString()} className="row g-0 border-top">
            <div
              className="col-5 d-flex py-2 align-items-center justify-content-between"
              style={{ background: '#F1F8FF' }}
            >
              <p className="p-small ps-4 w-50">Eligibility Report</p>
              <p className="p-small w-50 opacity-75">{time}</p>
            </div>
            <div
              className="col-7 d-flex py-2 align-items-center justify-content-between"
              style={{ background: '#F1F8FF' }}
            >
              <p className="p-small opacity-75">
                <span className="fw-bold">{action.intention}</span>
                {action.createdBy && <span> by {action.createdBy}</span>}
              </p>

              <div className="p-small d-flex justify-content-end pe-3">
                <Button size="small" type="link" onClick={() => downloadEligibilityPdf(action._id)}>
                  Download
                </Button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
